// BEE-17: TopNav — sticky dark header with mobile hamburger
import { useState } from 'react'
import { Link } from 'react-router-dom'

const NAV_LINKS = [
  { label: 'How it works', href: '#how-it-works' },
  { label: 'Features',     href: '#features'     },
  { label: 'Pricing',      href: '#pricing'      },
]

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a1f1c]'

export default function TopNav() {
  const [menuOpen, setMenuOpen] = useState(false)

  function closeMenu() {
    setMenuOpen(false)
  }

  return (
    <header className="bg-[#1a1f1c] sticky top-0 z-50 overflow-x-hidden">
      <div className="max-w-[1240px] mx-auto px-4 md:px-8 py-[13px] flex items-center gap-3 md:gap-8">

        {/* ── Brand ── */}
        <a
          href="/"
          className={`flex items-center gap-3 flex-none ${FOCUS_RING} rounded-md`}
        >
          <img src="/Logo1.png" alt="KindredCanopy mark" className="w-10 h-10 block" />
          <div>
            <div className="font-serif font-semibold text-[18px] sm:text-[21px] text-white leading-none tracking-[-0.01em]">
              KindredCanopy
            </div>
            <div className="hidden sm:block font-sans text-[10px] text-[#7a9080] tracking-[0.18em] uppercase mt-[3px]">
              Your village · synchronized
            </div>
          </div>
        </a>

        {/* ── Desktop nav links (centred) ── */}
        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center" aria-label="Primary">
          {NAV_LINKS.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              className={`px-[14px] py-2 rounded-full text-[14px] font-medium text-[#b8cabe] hover:text-white transition-colors duration-150 ${FOCUS_RING}`}
            >
              {label}
            </a>
          ))}
        </nav>

        {/* ── Spacer — pushes mobile actions to the right ── */}
        <div className="flex-1 md:hidden" aria-hidden="true" />

        {/* ── Desktop action buttons ── */}
        <div className="hidden md:flex items-center gap-3 flex-none">
          <Link
            to="/sign-in"
            className={`text-[14px] font-medium text-[#b8cabe] hover:text-white transition-colors duration-150 px-1 ${FOCUS_RING} rounded-md`}
          >
            Sign in
          </Link>
          <Link
            to="/enroll"
            className={`inline-flex items-center px-[18px] py-[9px] rounded-full bg-gold-500 text-[#2a1f08] text-[13.5px] font-semibold hover:bg-[#b88a2c] transition-colors duration-150 shadow-md ${FOCUS_RING}`}
          >
            Start for free
          </Link>
        </div>

        {/* ── Mobile CTA ── */}
        <Link
          to="/enroll"
          className={`md:hidden inline-flex items-center px-[14px] py-[8px] rounded-full bg-gold-500 text-[#2a1f08] text-[13px] font-semibold hover:bg-[#b88a2c] transition-colors duration-150 shadow-md flex-none ${FOCUS_RING}`}
        >
          Start free
        </Link>

        {/* ── Hamburger button ── */}
        <button
          type="button"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen(prev => !prev)}
          className={`md:hidden flex-none p-2 rounded-lg text-[#b8cabe] hover:text-white hover:bg-white/10 transition-colors duration-150 ${FOCUS_RING}`}
        >
          {menuOpen ? (
            /* X icon */
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            /* Hamburger icon */
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>

      </div>

      {/* ── Mobile menu drawer ── */}
      {menuOpen && (
        <div
          id="mobile-menu"
          className="md:hidden border-t border-white/10"
          aria-label="Mobile navigation"
        >
          <nav className="max-w-[1240px] mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map(({ label, href }) => (
              <a
                key={href}
                href={href}
                onClick={closeMenu}
                className={`px-4 py-3 rounded-xl text-[15px] font-medium text-[#b8cabe] hover:text-white hover:bg-white/[0.08] transition-colors duration-150 ${FOCUS_RING}`}
              >
                {label}
              </a>
            ))}
            <div className="border-t border-white/10 mt-2 pt-4 flex flex-col gap-2">
              <Link
                to="/sign-in"
                onClick={closeMenu}
                className={`px-4 py-3 rounded-xl text-[15px] font-medium text-[#b8cabe] hover:text-white hover:bg-white/[0.08] transition-colors duration-150 ${FOCUS_RING}`}
              >
                Sign in
              </Link>
              <Link
                to="/enroll"
                onClick={closeMenu}
                className={`inline-flex justify-center items-center px-6 py-3 rounded-full bg-gold-500 text-[#2a1f08] text-[15px] font-semibold hover:bg-[#b88a2c] transition-colors duration-150 shadow-md ${FOCUS_RING}`}
              >
                Start for free
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
